import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { showToast } from './Toast';
import ReferralStatsDisplay from './ReferralStatsDisplay';

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

const ReferralSettingsSection = () => {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);

  const referralLink = user?.id
    ? `${
        window.location.hostname === "localhost"
          ? "http://localhost:3000"
          : "https://pixleyai.com"
      }/storyverse?ref=${user.id}`
    : "";

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      showToast("Referral link copied to clipboard!", "success");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      showToast("Failed to copy link. Please try again.", "error");
    }
  };

  if (!user) {
    return (
      <div
        style={{
          background: "#FFFFFF",
          border: "2px solid rgba(11,26,51,0.08)",
          borderRadius: 18,
          padding: 24,
          textAlign: "center",
          boxShadow: "0px 3px 0px rgba(11,26,51,0.05)",
        }}
      >
        <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, margin: 0 }}>
          Please log in to view your referral information.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Display */}
      <ReferralStatsDisplay />

      {/* Share & Earn */}
      <div
        className="grid grid-cols-1 lg:grid-cols-5 gap-5"
      >
        {/* Link + Quick Share */}
        <div
          className="lg:col-span-3 relative overflow-hidden"
          style={{
            background: "#FFFFFF",
            border: "2px solid rgba(11,26,51,0.06)",
            borderRadius: 22,
            padding: 24,
            boxShadow: "0px 4px 0px rgba(11,26,51,0.06), 0px 12px 24px rgba(11,26,51,0.06)",
          }}
        >
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: "linear-gradient(90deg, #0065F8 0%, #00CAFF 100%)" }} />

          <span style={{ ...FREDOKA, color: "#0065F8", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>
            Share &amp; Earn
          </span>
          <h4 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 22, textTransform: "uppercase", letterSpacing: "-0.01em", margin: "4px 0 8px" }}>
            Your Invite Link
          </h4>
          <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 13, margin: "0 0 18px" }}>
            Share Pixley with friends — you both get <strong style={{ color: "#0B1A33" }}>150 stars</strong>
            <img src="/assets/pixley_stars.png" alt="" style={{ width: 14, height: 14, display: "inline", margin: "0 4px", verticalAlign: "text-bottom" }} />
            when they start a Pixley membership.
          </p>

          <label style={{ ...FREDOKA, display: "block", color: "#5A6B85", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>
            Your unique link
          </label>
          <div className="relative">
            <input
              readOnly
              type="text"
              value={referralLink}
              onClick={(e) => e.target.select()}
              style={{
                ...FREDOKA,
                width: "100%",
                background: "#F5FAFF",
                color: "#0B1A33",
                fontSize: 14,
                fontWeight: 600,
                border: "2px solid rgba(11,26,51,0.08)",
                borderRadius: 14,
                padding: "12px 56px 12px 16px",
                outline: "none",
              }}
            />
            <button
              onClick={handleCopyLink}
              aria-label={copied ? "Copied" : "Copy link"}
              className="transition active:scale-[0.94]"
              style={{
                position: "absolute",
                right: 6,
                top: "50%",
                transform: "translateY(-50%)",
                width: 40,
                height: 40,
                background: copied ? "#0C4A6E" : "#0065F8",
                color: "#FFFFFF",
                border: "none",
                borderRadius: 10,
                boxShadow: copied ? "0px 2px 0px #082F49" : "0px 2px 0px #073B8E",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              {copied ? (
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                </svg>
              )}
            </button>
          </div>

          <div className="mt-5">
            <label style={{ ...FREDOKA, display: "block", color: "#5A6B85", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>
              Quick share
            </label>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent("Check out Pixley AI! " + referralLink)}`, "_blank");
                }}
                className="flex-1 inline-flex items-center justify-center gap-2 transition active:scale-[0.97]"
                style={{
                  ...FREDOKA,
                  background: "#0B1A33",
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: 13,
                  border: "none",
                  borderRadius: 999,
                  padding: "11px 18px",
                  boxShadow: "0px 3px 0px rgba(0,0,0,0.30)",
                  cursor: "pointer",
                }}
              >
                <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
                Post on X
              </button>
              <button
                onClick={() => {
                  window.open(`https://wa.me/?text=${encodeURIComponent("Check out Pixley AI! " + referralLink)}`, "_blank");
                }}
                className="flex-1 inline-flex items-center justify-center gap-2 transition active:scale-[0.97]"
                style={{
                  ...FREDOKA,
                  background: "#25D366",
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: 13,
                  border: "none",
                  borderRadius: 999,
                  padding: "11px 18px",
                  boxShadow: "0px 3px 0px #16803F",
                  cursor: "pointer",
                }}
              >
                <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                WhatsApp
              </button>
            </div>
          </div>
        </div>

        {/* How it works */}
        <div
          className="lg:col-span-2 relative overflow-hidden"
          style={{
            background: "linear-gradient(180deg, #E0F2FE 0%, #BAE6FD 100%)",
            border: "2px solid rgba(2,132,199,0.25)",
            borderRadius: 22,
            padding: 24,
            boxShadow: "0px 4px 0px rgba(2,132,199,0.18), 0px 12px 24px rgba(2,132,199,0.12)",
          }}
        >
          <span style={{ ...FREDOKA, color: "#0369A1", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>
            How It Works
          </span>
          <h4 style={{ ...FREDOKA, color: "#0C4A6E", fontWeight: 700, fontSize: 20, textTransform: "uppercase", letterSpacing: "-0.01em", margin: "4px 0 14px" }}>
            3 simple steps
          </h4>

          <ol className="space-y-3" style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {[
              { n: 1, title: "Share your link", desc: "Send it via X, WhatsApp, or anywhere." },
              { n: 2, title: "Friend signs up", desc: "They join Pixley using your link." },
              { n: 3, title: "Both earn stars", desc: "150 stars each, up to 3 referrals a month." },
            ].map((step) => (
              <li key={step.n} className="flex items-start gap-3">
                <div
                  className="flex-shrink-0 flex items-center justify-center"
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: "#FFFFFF",
                    color: "#0369A1",
                    boxShadow: "0px 2px 0px rgba(3,105,161,0.28)",
                    ...FREDOKA,
                    fontWeight: 800,
                    fontSize: 13,
                  }}
                >
                  {step.n}
                </div>
                <div>
                  <div style={{ ...FREDOKA, color: "#0C4A6E", fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>{step.title}</div>
                  <div style={{ ...FREDOKA, color: "#075985", fontSize: 12, marginTop: 2 }}>{step.desc}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
};

export default ReferralSettingsSection;
